import { useState, useEffect, useRef } from 'react'
import { useClientAuth } from '../../context/ClientAuthContext'
import { FileText, Plus, Minus, Clock, CheckCircle, XCircle, AlertCircle, X, Play, Square } from 'lucide-react'
import './ClientPortal.css'

// Predefined campaign types
const CAMPAIGN_TYPES = [
  'Medicare',
  'Final Expense',
  'Auto Insurance',
  'Solar Panels',
  'MVA',
  'ACA',
  'Real Estate',
  'Diabetic',
  'Diabetes',
  'DNC',
  'Med Alert',
  'Home Insurance',
  'Other'
]

export default function ClientRequests() {
  const { clientApi } = useClientAuth()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    try {
      const data = await clientApi('/requests')
      if (data.success) setRequests(data.requests)
    } catch (error) {
      console.error('Failed to load requests:', error)
    }
    setLoading(false)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} />
      case 'in_progress': return <AlertCircle size={16} />
      case 'completed': return <CheckCircle size={16} />
      case 'approved': return <CheckCircle size={16} />
      case 'rejected': return <XCircle size={16} />
      default: return <FileText size={16} />
    }
  }

  const getStatusClass = (status) => {
    if (status === 'completed' || status === 'approved') return 'success'
    if (status === 'rejected') return 'error'
    if (status === 'in_progress') return 'warning'
    return 'pending'
  }

  const filteredRequests = requests.filter(req => {
    if (filter === 'all') return true
    return req.status === filter
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="client-loading">
        <div className="loading-spinner"></div>
        <p>Loading requests...</p>
      </div>
    )
  }

  return (
    <div className="client-requests-page">
      <div className="client-page-header">
        <h1>Requests</h1>
        <p>Submit and track your bot requests</p>
      </div>

      {/* Stats */}
      <div className="client-stat-blocks">
        <div className="client-stat-block purple">
          <div className="client-stat-icon">
            <FileText size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">{requests.length}</div>
            <div className="client-stat-label">Total Requests</div>
          </div>
        </div>

        <div className="client-stat-block orange">
          <div className="client-stat-icon">
            <Clock size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">
              {requests.filter(r => r.status === 'pending' || r.status === 'in_progress').length}
            </div>
            <div className="client-stat-label">Pending</div>
          </div>
        </div>

        <div className="client-stat-block green">
          <div className="client-stat-icon">
            <CheckCircle size={22} />
          </div>
          <div className="client-stat-content">
            <div className="client-stat-value">
              {requests.filter(r => r.status === 'completed' || r.status === 'approved').length}
            </div>
            <div className="client-stat-label">Completed</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="client-toolbar">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`filter-tab ${filter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setFilter('in_progress')}
          >
            In Progress
          </button>
          <button
            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>

        <button className="new-request-btn" onClick={() => setShowModal(true)}>
          <Plus size={18} />
          <span>New Request</span>
        </button>
      </div>

      {/* Requests Table */}
      <div className="client-table-container">
        <table className="client-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Details</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  No requests found
                </td>
              </tr>
            ) : (
              filteredRequests.map(req => (
                <tr key={req.id}>
                  <td>#{req.id}</td>
                  <td>
                    <span className={`request-type ${req.type}`}>
                      {req.type === 'bot_addition' ? 'Bot Addition' : 'Bot Removal'}
                    </span>
                  </td>
                  <td>
                    <div className="request-details">
                      {req.type === 'bot_addition' ? (
                        <>
                          <span>{req.bot_count || '?'} bots</span>
                          {req.campaign && <span> • {req.campaign}</span>}
                        </>
                      ) : (
                        <span>Remove bots</span>
                      )}
                    </div>
                    {req.comments && (
                      <div className="request-comment">{req.comments}</div>
                    )}
                  </td>
                  <td>
                    <span className={`priority-badge ${req.priority}`}>
                      {req.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`client-status ${getStatusClass(req.status)}`}>
                      {getStatusIcon(req.status)}
                      {req.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{formatDate(req.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* New Request Modal */}
      {showModal && (
        <RequestModal
          onClose={() => setShowModal(false)}
          onSubmit={() => {
            setShowModal(false)
            loadRequests()
          }}
        />
      )}

      <style>{`
        .client-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          gap: 16px;
        }

        .filter-tabs {
          display: flex;
          gap: 4px;
          background: var(--bg-elevated);
          padding: 4px;
          border-radius: 10px;
          border: 1px solid var(--border-color);
        }

        .filter-tab {
          padding: 8px 16px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: var(--text-muted);
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-tab:hover { background: var(--bg-card-hover); }
        .filter-tab.active {
          background: rgba(59, 130, 246, 0.15);
          color: #3b82f6;
        }

        .new-request-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .new-request-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
        }

        .request-type {
          font-size: 13px;
          font-weight: 500;
        }

        .request-type.bot_addition { color: #22c55e; }
        .request-type.bot_removal { color: #f87171; }

        .request-details {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .request-comment {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 4px;
        }

        .priority-badge {
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .priority-badge.low { background: rgba(100, 116, 139, 0.15); color: #64748b; }
        .priority-badge.normal { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
        .priority-badge.high { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
        .priority-badge.urgent { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

        .client-status.success { color: #22c55e; }
        .client-status.error { color: #ef4444; }
        .client-status.warning { color: #f59e0b; }
        .client-status.pending { color: var(--text-muted); }

        .client-status svg { margin-right: 6px; }
      `}</style>
    </div>
  )
}

function RequestModal({ onClose, onSubmit }) {
  const { clientApi } = useClientAuth()
  const [type, setType] = useState('bot_addition')
  const [dialers, setDialers] = useState([])
  const [voiceSets, setVoiceSets] = useState([])
  const [loadingData, setLoadingData] = useState(true)
  const [selectedDialer, setSelectedDialer] = useState('')
  const [isNewDialer, setIsNewDialer] = useState(false)

  // Audio preview state
  const audioRef = useRef(null)
  const [playingVoiceSet, setPlayingVoiceSet] = useState(null)
  const [loadingAudio, setLoadingAudio] = useState(null)

  const [formData, setFormData] = useState({
    priority: 'normal',
    // General Info
    bot_count: '',
    campaign: '',
    // IP Authentication
    whitelist_url: '',
    whitelist_username: '',
    whitelist_password: '',
    // SIP Information
    dialer_url: '',
    sip_range: '',
    sip_password: '',
    sip_password_same: false,
    transfer_method: '',
    // Voice Sets
    voice_set_ids: [],
    // Comments
    comments: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Load dialers and voice sets on mount
  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    setLoadingData(true)
    try {
      const [dialersRes, voiceSetsRes] = await Promise.all([
        clientApi('/dialers'),
        clientApi('/voice-sets')
      ])
      if (dialersRes.success) setDialers(dialersRes.dialers || [])
      if (voiceSetsRes.success) setVoiceSets(voiceSetsRes.voice_sets || [])
    } catch (err) {
      console.error('Failed to load data:', err)
    }
    setLoadingData(false)
  }

  // Handle dialer selection
  const handleDialerChange = (value) => {
    setSelectedDialer(value)
    if (value === 'other') {
      setIsNewDialer(true)
      setFormData(prev => ({
        ...prev,
        whitelist_url: '',
        whitelist_username: '',
        whitelist_password: '',
        dialer_url: '',
        transfer_method: '',
        voice_set_ids: []
      }))
    } else if (value) {
      setIsNewDialer(false)
      const dialer = dialers.find(d => d.id === parseInt(value))
      if (dialer) {
        // Pre-fill from existing dialer template
        const voiceSetPool = dialer.voice_set_pool ?
          (typeof dialer.voice_set_pool === 'string' ?
            (dialer.voice_set_pool.startsWith('[') ? JSON.parse(dialer.voice_set_pool) : dialer.voice_set_pool.split(',').map(id => parseInt(id.trim())))
            : dialer.voice_set_pool)
          : []
        // Construct dialer URL from access_link if available (replace ip_whitelist.php with vicidial.php)
        let dialerUrl = ''
        if (dialer.access_link) {
          dialerUrl = dialer.access_link.replace(/ip_whitelist\.php.*$/, 'vicidial.php')
        }
        setFormData(prev => ({
          ...prev,
          campaign: dialer.campaign_name || dialer.campaign || prev.campaign,
          whitelist_url: dialer.access_link || '',
          dialer_url: dialerUrl,
          transfer_method: dialer.transfer_method || '',
          voice_set_ids: voiceSetPool
        }))
      }
    } else {
      setIsNewDialer(false)
    }
  }

  // Voice set quick selection
  const selectAllVoiceSets = () => {
    setFormData(prev => ({ ...prev, voice_set_ids: voiceSets.map(vs => vs.id) }))
  }
  const selectMaleVoiceSets = () => {
    setFormData(prev => ({ ...prev, voice_set_ids: voiceSets.filter(vs => vs.gender?.toLowerCase() === 'male').map(vs => vs.id) }))
  }
  const selectFemaleVoiceSets = () => {
    setFormData(prev => ({ ...prev, voice_set_ids: voiceSets.filter(vs => vs.gender?.toLowerCase() === 'female').map(vs => vs.id) }))
  }
  const toggleVoiceSet = (vsId) => {
    setFormData(prev => ({
      ...prev,
      voice_set_ids: prev.voice_set_ids.includes(vsId)
        ? prev.voice_set_ids.filter(id => id !== vsId)
        : [...prev.voice_set_ids, vsId]
    }))
  }

  // Audio preview functions
  const playVoiceSetPreview = async (vsId, e) => {
    e.stopPropagation() // Don't toggle selection when clicking play

    // If already playing this voice set, stop it
    if (playingVoiceSet === vsId) {
      stopAudio()
      return
    }

    // Stop any currently playing audio
    stopAudio()

    setLoadingAudio(vsId)

    try {
      // Get sample audio URL with campaign category filter
      const categoryParam = formData.campaign && formData.campaign !== 'Other'
        ? `?category=${encodeURIComponent(formData.campaign)}`
        : ''

      const data = await clientApi(`/voice-sets/${vsId}/sample${categoryParam}`)

      if (data.success && data.audio_url) {
        // Create and play audio
        const audio = new Audio(`https://dashboard.byredstone.com${data.audio_url}`)
        audioRef.current = audio

        audio.onended = () => {
          setPlayingVoiceSet(null)
        }

        audio.onerror = () => {
          console.error('Audio playback error')
          setPlayingVoiceSet(null)
        }

        await audio.play()
        setPlayingVoiceSet(vsId)
      } else {
        console.error('No audio available:', data.error)
      }
    } catch (err) {
      console.error('Failed to load audio:', err)
    }

    setLoadingAudio(null)
  }

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    setPlayingVoiceSet(null)
  }

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (type === 'bot_addition') {
      if (!formData.bot_count) return setError('Number of bots is required')
      if (!formData.campaign) return setError('Campaign name is required')
      if (!selectedDialer) return setError('Please select a dialer')
      if (!formData.whitelist_url) return setError('Whitelist URL is required')
      if (!formData.whitelist_username) return setError('Whitelist username is required')
      if (!formData.whitelist_password) return setError('Whitelist password is required')
      if (!formData.dialer_url) return setError('Dialer URL is required')
      if (!formData.sip_range) return setError('SIP ID / Range is required')
      if (!formData.sip_password && !formData.sip_password_same) return setError('SIP password is required')
    } else {
      if (!selectedDialer || selectedDialer === 'other') return setError('Please select a dialer')
      if (!formData.sip_range) return setError('SIP IDs are required')
    }

    setSubmitting(true)

    try {
      const submitData = {
        type,
        priority: formData.priority,
        dialer_id: selectedDialer !== 'other' ? parseInt(selectedDialer) : null,
        is_new_dialer: isNewDialer,
        bot_count: formData.bot_count ? parseInt(formData.bot_count) : null,
        campaign: formData.campaign,
        whitelist_url: formData.whitelist_url,
        whitelist_username: formData.whitelist_username,
        whitelist_password: formData.whitelist_password,
        dialer_url: formData.dialer_url,
        sip_range: formData.sip_range,
        sip_password: formData.sip_password_same ? '[SAME_AS_SIP]' : formData.sip_password,
        transfer_method: formData.transfer_method,
        voice_set_ids: JSON.stringify(formData.voice_set_ids),
        comments: formData.comments
      }

      const data = await clientApi('/requests', {
        method: 'POST',
        body: JSON.stringify(submitData)
      })

      if (data.success) {
        onSubmit()
      } else {
        setError(data.error || 'Failed to submit request')
      }
    } catch (err) {
      setError(err.message || 'Failed to submit request')
    }

    setSubmitting(false)
  }

  // Group voice sets by gender
  const maleVoiceSets = voiceSets.filter(vs => vs.gender?.toLowerCase() === 'male')
  const femaleVoiceSets = voiceSets.filter(vs => vs.gender?.toLowerCase() === 'female')

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content request-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Request</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {loadingData ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div className="client-error-message">{error}</div>}

            {/* Request Type Toggle */}
            <div className="form-group">
              <label>Request Type</label>
              <div className="type-selector">
                <button
                  type="button"
                  className={`type-btn ${type === 'bot_addition' ? 'active' : ''}`}
                  onClick={() => { setType('bot_addition'); setSelectedDialer(''); setIsNewDialer(false); }}
                >
                  <Plus size={16} /> Bot Addition
                </button>
                <button
                  type="button"
                  className={`type-btn removal ${type === 'bot_removal' ? 'active' : ''}`}
                  onClick={() => { setType('bot_removal'); setSelectedDialer(''); setIsNewDialer(false); }}
                >
                  <Minus size={16} /> Bot Removal
                </button>
              </div>
            </div>

            {/* Priority */}
            <div className="form-group">
              <label>Priority</label>
              <select
                value={formData.priority}
                onChange={e => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* ============ BOT ADDITION FORM ============ */}
            {type === 'bot_addition' && (
              <>
                {/* Section: General Information */}
                <div className="form-section">
                  <h3 className="form-section-title">General Information</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Number of Bots <span className="required">*</span></label>
                      <input
                        type="number"
                        value={formData.bot_count}
                        onChange={e => setFormData(prev => ({ ...prev, bot_count: e.target.value }))}
                        placeholder="e.g., 10"
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Campaign Type <span className="required">*</span></label>
                      <select
                        value={formData.campaign}
                        onChange={e => setFormData(prev => ({ ...prev, campaign: e.target.value }))}
                      >
                        <option value="">Select campaign type...</option>
                        {CAMPAIGN_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Dialer <span className="required">*</span></label>
                    <select value={selectedDialer} onChange={e => handleDialerChange(e.target.value)}>
                      <option value="">Select a dialer...</option>
                      {dialers.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                      <option value="other">➕ Other (New Dialer)</option>
                    </select>
                    {isNewDialer && (
                      <div className="form-hint warning">New dialer - please fill in all configuration details below</div>
                    )}
                  </div>
                </div>

                {/* Section: IP Authentication */}
                <div className="form-section">
                  <h3 className="form-section-title">IP Authentication</h3>

                  <div className="form-group">
                    <label>Self Whitelist URL <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.whitelist_url}
                      onChange={e => setFormData(prev => ({ ...prev, whitelist_url: e.target.value }))}
                      placeholder="https://dialer.example.com/vicidial/ip_whitelist.php"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Username <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.whitelist_username}
                        onChange={e => setFormData(prev => ({ ...prev, whitelist_username: e.target.value }))}
                        placeholder="Whitelist username"
                      />
                    </div>
                    <div className="form-group">
                      <label>Password <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.whitelist_password}
                        onChange={e => setFormData(prev => ({ ...prev, whitelist_password: e.target.value }))}
                        placeholder="Whitelist password"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: SIP Information */}
                <div className="form-section">
                  <h3 className="form-section-title">SIP Information</h3>

                  <div className="form-group">
                    <label>Dialer URL <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.dialer_url}
                      onChange={e => setFormData(prev => ({ ...prev, dialer_url: e.target.value }))}
                      placeholder="https://dialer.example.com/vicidial/vicidial.php"
                    />
                    <div className="form-hint">Must end with /vicidial.php</div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>SIP ID / Range <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.sip_range}
                        onChange={e => setFormData(prev => ({ ...prev, sip_range: e.target.value }))}
                        placeholder="e.g., 1001-1050 or 1001,1002,1003"
                      />
                    </div>
                    <div className="form-group">
                      <label>SIP Password <span className="required">*</span></label>
                      <input
                        type="text"
                        value={formData.sip_password}
                        onChange={e => setFormData(prev => ({ ...prev, sip_password: e.target.value }))}
                        placeholder="SIP password"
                        disabled={formData.sip_password_same}
                      />
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.sip_password_same}
                          onChange={e => setFormData(prev => ({ ...prev, sip_password_same: e.target.checked, sip_password: '' }))}
                        />
                        Password same as SIP ID
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Transfer Method</label>
                    <input
                      type="text"
                      value={formData.transfer_method}
                      onChange={e => setFormData(prev => ({ ...prev, transfer_method: e.target.value }))}
                      placeholder="e.g., Blind Transfer, Warm Transfer, D1"
                    />
                  </div>
                </div>

                {/* Section: Voice Sets */}
                <div className="form-section">
                  <h3 className="form-section-title">Voice Set Selection</h3>

                  <div className="voice-set-quick-select">
                    <button type="button" className="quick-btn" onClick={selectAllVoiceSets}>All Voice Sets</button>
                    <button type="button" className="quick-btn male" onClick={selectMaleVoiceSets}>All Male</button>
                    <button type="button" className="quick-btn female" onClick={selectFemaleVoiceSets}>All Female</button>
                    <button type="button" className="quick-btn clear" onClick={() => setFormData(prev => ({ ...prev, voice_set_ids: [] }))}>Clear</button>
                  </div>

                  <div className="voice-set-grid">
                    {voiceSets.map(vs => (
                      <div
                        key={vs.id}
                        className={`voice-set-item ${formData.voice_set_ids.includes(vs.id) ? 'selected' : ''} ${playingVoiceSet === vs.id ? 'playing' : ''}`}
                        onClick={() => toggleVoiceSet(vs.id)}
                      >
                        <div className="vs-info">
                          <span className="vs-name">{vs.name}</span>
                          <span className={`vs-gender ${vs.gender?.toLowerCase()}`}>{vs.gender}</span>
                        </div>
                        <div className="vs-actions">
                          <button
                            type="button"
                            className={`vs-play-btn ${playingVoiceSet === vs.id ? 'playing' : ''}`}
                            onClick={(e) => playVoiceSetPreview(vs.id, e)}
                            disabled={loadingAudio === vs.id}
                            title={playingVoiceSet === vs.id ? 'Stop preview' : 'Play preview'}
                          >
                            {loadingAudio === vs.id ? (
                              <div className="vs-loading-spinner"></div>
                            ) : playingVoiceSet === vs.id ? (
                              <Square size={12} />
                            ) : (
                              <Play size={12} />
                            )}
                          </button>
                          {formData.voice_set_ids.includes(vs.id) && <CheckCircle size={14} className="vs-check" />}
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.voice_set_ids.length > 0 && (
                    <div className="voice-set-count">{formData.voice_set_ids.length} voice set(s) selected</div>
                  )}
                </div>
              </>
            )}

            {/* ============ BOT REMOVAL FORM ============ */}
            {type === 'bot_removal' && (
              <>
                <div className="form-section">
                  <h3 className="form-section-title">Removal Details</h3>

                  <div className="form-group">
                    <label>Dialer <span className="required">*</span></label>
                    <select value={selectedDialer} onChange={e => setSelectedDialer(e.target.value)}>
                      <option value="">Select a dialer...</option>
                      {dialers.map(d => (
                        <option key={d.id} value={d.id}>{d.name} ({d.total_bots} bots)</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>SIP IDs to Remove <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.sip_range}
                      onChange={e => setFormData(prev => ({ ...prev, sip_range: e.target.value }))}
                      placeholder="e.g., 1001-1010 or 1001,1002,1003"
                    />
                    <div className="form-hint">Enter SIP IDs as a range (1001-1010) or comma-separated list</div>
                  </div>
                </div>
              </>
            )}

            {/* Additional Comments */}
            <div className="form-section">
              <h3 className="form-section-title">Additional Information</h3>
              <div className="form-group">
                <label>Comments</label>
                <textarea
                  value={formData.comments}
                  onChange={e => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Any additional information or special requirements..."
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .request-modal {
            background: var(--bg-card);
            border: 1px solid var(--border-color);
            border-radius: 16px;
            width: 100%;
            max-width: 680px;
            max-height: calc(100vh - 40px);
            overflow-y: auto;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid var(--border-color);
            position: sticky;
            top: 0;
            background: var(--bg-card);
            z-index: 10;
          }

          .modal-header h2 {
            font-size: 18px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .modal-close {
            background: none;
            border: none;
            color: var(--text-muted);
            cursor: pointer;
            padding: 4px;
          }

          .modal-close:hover { color: var(--text-primary); }

          .modal-loading {
            padding: 60px 24px;
            text-align: center;
            color: var(--text-muted);
          }

          .request-modal form {
            padding: 24px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          /* Form Sections */
          .form-section {
            background: var(--bg-elevated);
            border: 1px solid var(--border-color);
            border-radius: 12px;
            padding: 16px;
          }

          .form-section-title {
            font-size: 14px;
            font-weight: 600;
            color: var(--text-primary);
            margin: 0 0 16px 0;
            padding-bottom: 12px;
            border-bottom: 1px solid var(--border-color);
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: 6px;
            margin-bottom: 12px;
          }

          .form-group:last-child { margin-bottom: 0; }

          .form-group label {
            font-size: 13px;
            font-weight: 500;
            color: var(--text-secondary);
          }

          .form-group label .required {
            color: #ef4444;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            padding: 10px 12px;
            background: var(--bg-input, #1a1a1a);
            border: 1px solid var(--border-color);
            border-radius: 8px;
            color: var(--text-primary);
            font-size: 14px;
          }

          .form-group select {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 12px center;
            padding-right: 36px;
            cursor: pointer;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: var(--primary);
          }

          .form-group input:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .form-hint {
            font-size: 11px;
            color: var(--text-muted);
            margin-top: 4px;
          }

          .form-hint.warning {
            color: #f59e0b;
            background: rgba(245, 158, 11, 0.1);
            padding: 8px 12px;
            border-radius: 6px;
            margin-top: 8px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: var(--text-muted);
            cursor: pointer;
            margin-top: 6px;
          }

          .checkbox-label input[type="checkbox"] {
            width: 16px;
            height: 16px;
            margin: 0;
            padding: 0;
            cursor: pointer;
          }

          .type-selector {
            display: flex;
            gap: 8px;
          }

          .type-btn {
            flex: 1;
            padding: 12px;
            background: var(--bg-input);
            border: 1px solid var(--border-color);
            border-radius: 8px;
            color: var(--text-muted);
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .type-btn.active {
            background: rgba(34, 197, 94, 0.1);
            border-color: #22c55e;
            color: #22c55e;
          }

          .type-btn.removal.active {
            background: rgba(239, 68, 68, 0.1);
            border-color: #ef4444;
            color: #ef4444;
          }

          /* Voice Set Selection */
          .voice-set-quick-select {
            display: flex;
            gap: 8px;
            margin-bottom: 12px;
            flex-wrap: wrap;
          }

          .quick-btn {
            padding: 6px 12px;
            background: var(--bg-input);
            border: 1px solid var(--border-color);
            border-radius: 6px;
            color: var(--text-muted);
            font-size: 12px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .quick-btn:hover { border-color: var(--text-secondary); }
          .quick-btn.male:hover { border-color: #3b82f6; color: #3b82f6; }
          .quick-btn.female:hover { border-color: #ec4899; color: #ec4899; }
          .quick-btn.clear:hover { border-color: #ef4444; color: #ef4444; }

          .voice-set-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
            gap: 8px;
            max-height: 240px;
            overflow-y: auto;
            padding: 4px;
          }

          .voice-set-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 10px;
            background: var(--bg-input);
            border: 1px solid var(--border-color);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;
            gap: 8px;
          }

          .voice-set-item:hover {
            border-color: var(--text-muted);
          }

          .voice-set-item.selected {
            background: rgba(34, 197, 94, 0.1);
            border-color: #22c55e;
          }

          .voice-set-item.playing {
            border-color: #3b82f6;
            box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.3);
          }

          .vs-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            flex: 1;
            min-width: 0;
          }

          .vs-name {
            font-size: 12px;
            color: var(--text-primary);
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .vs-gender {
            font-size: 9px;
            padding: 1px 5px;
            border-radius: 3px;
            text-transform: uppercase;
            font-weight: 600;
            width: fit-content;
          }

          .vs-gender.male {
            background: rgba(59, 130, 246, 0.15);
            color: #3b82f6;
          }

          .vs-gender.female {
            background: rgba(236, 72, 153, 0.15);
            color: #ec4899;
          }

          .vs-actions {
            display: flex;
            align-items: center;
            gap: 6px;
            flex-shrink: 0;
          }

          .vs-play-btn {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: rgba(59, 130, 246, 0.15);
            border: 1px solid rgba(59, 130, 246, 0.3);
            color: #3b82f6;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s;
          }

          .vs-play-btn:hover {
            background: rgba(59, 130, 246, 0.25);
            border-color: #3b82f6;
          }

          .vs-play-btn.playing {
            background: rgba(239, 68, 68, 0.15);
            border-color: rgba(239, 68, 68, 0.3);
            color: #ef4444;
          }

          .vs-play-btn.playing:hover {
            background: rgba(239, 68, 68, 0.25);
            border-color: #ef4444;
          }

          .vs-play-btn:disabled {
            opacity: 0.7;
            cursor: wait;
          }

          .vs-loading-spinner {
            width: 10px;
            height: 10px;
            border: 2px solid rgba(59, 130, 246, 0.3);
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .vs-check {
            color: #22c55e;
            flex-shrink: 0;
          }

          .voice-set-count {
            font-size: 12px;
            color: #22c55e;
            margin-top: 8px;
            text-align: center;
          }

          .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 8px;
            padding-top: 16px;
            border-top: 1px solid var(--border-color);
          }

          .btn-secondary {
            padding: 10px 20px;
            background: var(--bg-elevated);
            border: 1px solid var(--border-color);
            border-radius: 8px;
            color: var(--text-muted);
            font-size: 14px;
            cursor: pointer;
          }

          .btn-secondary:hover {
            border-color: var(--text-muted);
          }

          .btn-primary {
            padding: 10px 20px;
            background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
            border: none;
            border-radius: 8px;
            color: white;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
          }

          .btn-primary:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          @media (max-width: 600px) {
            .form-row {
              grid-template-columns: 1fr;
            }
            .voice-set-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  )
}
